// @flow
import * as React from 'react'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import styled, { createGlobalStyle } from 'styled-components'
import HtmlHead from '@components/HtmlHead'
import type { AppStateType } from '../../reducers/appstate'
import { withRouter } from 'next/router'
import {
  CTASecondary,
  GlobalStyle,
  GoodSansB5,
  GoodSansLink,
  P14,
  QueensH4
} from '@components/shared'
import { colors } from '@components/shared/colors'
import { config, Container, Col, Row } from 'react-awesome-styled-grid'
import AuthFormContainer from '@components/auth/AuthForm'
import SimpleNav from '@components/SimpleNav'
import { LinkWithUtms } from '../../actions/navigationActions'
import { PrimaryCta } from '../../pages/account/refund'
import BackgroundGradient from '@components/BackgroundGradient'

type PropsType = {
  userId?: string,
  isSubscribed: boolean,
  title: string,
  pageDescription: string,
  children: React.Node,
  breadCrumbTitle: string,
  lastPaymentSuccessful?: boolean,
  passwordReset?: boolean,
  updateCardScreen?: boolean
}

type DispatchPropsType = {
}

function AccountManagement (props: PropsType & DispatchPropsType): React.Node {
  return (
    <PageContainer>
      <HtmlHead
        title={props.title}
        description={props.pageDescription}
        includeStripe={true}
      />
      <GlobalStyle backgroundColor={colors.midnightBlue}/>
      <AccountManagementBody/>
      <BackgroundGradient quadrant={3} />
      <SimpleNav backgroundColor={colors.midnightBlue95} buttonOrder={['listen', 'sign-out']}/>
      <Container>
        <Row justify='center'>
          <Col xs={4} sm={6} md={6} lg={8} xl={4} align={'center'}>
            <BreadcrumbsContainer>
              {props.breadCrumbTitle && (
                <>
                  <LinkWithUtms pathname={'/account/'}>
                    <BreadCrumbLink>Account</BreadCrumbLink>
                  </LinkWithUtms>
                  <BreadCrumbTitle>
                    <span>|</span>
                    {props.breadCrumbTitle}
                  </BreadCrumbTitle>
                </>)
              }
            </BreadcrumbsContainer>
            {(props.lastPaymentSuccessful === false && !props.updateCardScreen) && (
              <UpdateCardModal>
                <OopsModalTitle>Oops! There was a billing issue with your payment method</OopsModalTitle>
                <LinkWithUtms pathname={'/account/update-card'}>
                  <PrimaryCta>UPDATE CARD</PrimaryCta>
                </LinkWithUtms>
              </UpdateCardModal>
            )}
            <AuthContainer>
              {!props.userId && !props.passwordReset && <>
                <P14 color={colors.sand}>Sign In</P14>
                <AuthFormContainer theme="sand" page={'signIn'}/>
              </>
              }
              {props.userId && props.children}
              {props.passwordReset && props.children}
            </AuthContainer>
            <Terms>
              <LinkWithUtms pathname={'https://www.dipseastories.com/privacypolicy/'}>
                <Link>PRIVACY POLICY</Link>
              </LinkWithUtms>
              <LinkWithUtms pathname={'https://www.dipseastories.com/terms/'}>
                <Link>TERMS & CONDITIONS</Link>
              </LinkWithUtms>
              <LinkWithUtms pathname={'https://support.dipseastories.com/en/'}>
                <Link>HELP CENTER</Link>
              </LinkWithUtms>
              <LinkWithUtms pathname={'https://www.dipseastories.com/blog/dipsea-faq/'}>
                <Link>FAQ</Link>
              </LinkWithUtms>
            </Terms>
            {props.userId && !props.passwordReset && <LinkWithUtms pathname={'/account/delete'}>
              <DeleteAccountCTA>Delete Account</DeleteAccountCTA>
            </LinkWithUtms>}
          </Col>
        </Row>
      </Container>
    </PageContainer>
  )
}

type OwnPropsType = {
  title: string,
  description: string,
  children: React.Node,
  breadCrumbTitle: string,
  passwordReset?: boolean
}

const mapStateToProps = (state: AppStateType, ownProps: OwnPropsType): any => {
  return {
    userId: state.user?.id,
    isSubscribed: state.user?.isSubscribed || false,
    title: ownProps.title,
    description: ownProps.description,
    children: ownProps.children,
    breadCrumbTitle: ownProps.breadCrumbTitle,
    lastPaymentSuccessful: state.subscriptionPurchase?.lastPaymentSuccessful,
    passwordReset: ownProps.passwordReset
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchPropsType => {
  return {
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AccountManagement))

const PageContainer = styled.div`
  height: 100%;
`
const AuthContainer = styled.div`
  display: flex;
  padding: 32px 22px 32px 22px;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid ${colors.sand};
  margin-top: 16px;
  margin-bottom: 32px;
  align-items:center;
  ${(props: any): string => config(props).media.sm`
    width: 100%;
  `}
  ${(props: any): string => config(props).media.lg`
    max-width: 525px;
    align-items: flex-start;
  `}
  z-index: 1;
`

const AccountManagementBody = createGlobalStyle`
  body {
    background-color: ${colors.midnightBlue};
  }
`

const Terms = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
`

const Link = styled.div`
  ${GoodSansLink};
  margin-right: 16px;
  text-align: center;
  text-transform: uppercase;
  @media (min-width: 1024px) {
    font-size: 12px;
  }
`

const DeleteAccountCTA = styled(CTASecondary)`
  margin-top: 32px;
`

const BreadcrumbsContainer = styled.div`
  margin-top: 60px;
  width: 100%;
  display: flex;
  flex-direction: row;
  span {
    margin-left: 4px;
    margin-right: 4px;
  }
`

const BreadCrumbLink = styled.div`
  ${GoodSansB5};
  color: ${colors.tintOrange};
  text-decoration: underline;
`
const BreadCrumbTitle = styled.p`
  ${GoodSansB5};
  color: ${colors.white70};
`

const UpdateCardModal = styled.div`
  border-radius: 10px;
  padding: 32px 22px 32px 22px;
  background-color: ${colors.accentBlue};
  width: 100%;
  margin-top: 32px;
`
const OopsModalTitle = styled.h2`
  ${QueensH4};
  font-size: 24px;
`
